import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Chip,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import LeadService from "../services/LeadService";
import {
  REMINDER_FREQUENCIES,
  REMINDER_TYPES,
  CALL_DUE_OPTIONS,
  DAY_NAMES,
  MONTH_NAMES,
  formatReminderTime,
  formatReminderSchedule,
  formatReminderType,
} from "./leadConstants";

const PURPLE = "#5c4fd6";

const emptyForm = () => ({
  reminder_type: "notification",
  due_option: "1h",
  frequency: "daily",
  time: "09:00",
  day_of_week: 1,
  day_of_month: 1,
  month: 1,
});

const LeadRemindersEditor = ({
  reminders = [],
  draftMode = false,
  leadId = null,
  onDraftChange,
  onRefresh,
  compact = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const isCall = form.reminder_type === "call";

  const openAdd = () => {
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const buildPayload = () => {
    if (isCall) {
      return {
        reminder_type: "call",
        due_option: form.due_option,
      };
    }
    const payload = {
      reminder_type: "notification",
      frequency: form.frequency,
      time_of_day: `${form.time}:00`,
    };
    if (form.frequency === "weekly") {
      payload.day_of_week = form.day_of_week;
    }
    if (form.frequency === "yearly") {
      payload.month = form.month;
      payload.day_of_month = form.day_of_month;
    }
    return payload;
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (draftMode) {
      onDraftChange?.([
        ...reminders,
        { ...payload, _key: `draft_${Date.now()}` },
      ]);
      setDialogOpen(false);
      return;
    }
    if (!leadId) return;
    setSaving(true);
    try {
      await LeadService.createReminder(leadId, payload);
      setDialogOpen(false);
      onRefresh?.();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reminder) => {
    if (draftMode) {
      onDraftChange?.(reminders.filter((r) => r._key !== reminder._key));
      return;
    }
    if (!leadId || !reminder.id) return;
    await LeadService.deleteReminder(leadId, reminder.id);
    onRefresh?.();
  };

  const rowKey = (reminder) => reminder.id || reminder._key;

  return (
    <Box>
      {!compact && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Reminders
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={openAdd}>
            Add reminder
          </Button>
        </Box>
      )}

      {reminders.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: compact ? 1 : 0 }}>
          No reminders set
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ mb: compact ? 1 : 0, borderColor: "#eceaf5" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8f7fc" }}>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>When</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Schedule</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Time</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {reminders.map((reminder) => {
                const call = reminder.reminder_type === "call" || reminder.frequency === "once";
                return (
                  <TableRow key={rowKey(reminder)}>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      <Chip
                        size="small"
                        label={formatReminderType(reminder.reminder_type)}
                        color={call ? "success" : "default"}
                        variant={call ? "filled" : "outlined"}
                        sx={{ fontSize: "0.7rem", height: 22 }}
                      />
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize", fontSize: "0.8rem" }}>
                      {call ? "Once" : reminder.frequency}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {formatReminderSchedule(reminder)}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {call
                        ? (reminder.next_fire_at
                          ? new Date(reminder.next_fire_at).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })
                          : "—")
                        : formatReminderTime(reminder.time_of_day)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleDelete(reminder)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {compact ? (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ color: PURPLE, fontWeight: 600 }}
        >
          Add reminder
        </Button>
      ) : null}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add reminder</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={form.reminder_type}
              label="Type"
              onChange={(e) => setForm({ ...form, reminder_type: e.target.value })}
            >
              {REMINDER_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {isCall ? (
            <FormControl fullWidth>
              <InputLabel>Remind me</InputLabel>
              <Select
                value={form.due_option}
                label="Remind me"
                onChange={(e) => setForm({ ...form, due_option: e.target.value })}
              >
                {CALL_DUE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={form.frequency}
                  label="Frequency"
                  onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                >
                  {REMINDER_FREQUENCIES.map((f) => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {form.frequency === "weekly" && (
                <FormControl fullWidth>
                  <InputLabel>Day</InputLabel>
                  <Select
                    value={form.day_of_week}
                    label="Day"
                    onChange={(e) => setForm({ ...form, day_of_week: e.target.value })}
                  >
                    {DAY_NAMES.map((name, idx) => (
                      <MenuItem key={name} value={idx}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {form.frequency === "yearly" && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={form.month}
                      label="Month"
                      onChange={(e) => setForm({ ...form, month: e.target.value })}
                    >
                      {MONTH_NAMES.map((name, idx) => (
                        <MenuItem key={name} value={idx + 1}>{name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Day of month"
                    type="number"
                    inputProps={{ min: 1, max: 31 }}
                    value={form.day_of_month}
                    onChange={(e) => setForm({ ...form, day_of_month: Number(e.target.value) })}
                    fullWidth
                  />
                </>
              )}

              <TextField
                label="Time"
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: PURPLE }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadRemindersEditor;
